import { functions } from "../../../../config/firebaseConfig";
import { httpsCallable } from "firebase/functions";

const feedbackQueue = [];
let isProcessingQueue = false;
const MAX_CONCURRENT_REQUESTS = 3;
let activeRequests = 0;

export const addToFeedbackQueue = (
  questionId,
  questionType,
  questionData,
  userAnswer,
  setAiFeedback
) => {
  feedbackQueue.push({
    questionId,
    questionType,
    questionData,
    userAnswer,
    callback: setAiFeedback,
  });
  if (!isProcessingQueue) {
    processQueue();
  }
};

const processQueue = async () => {
  isProcessingQueue = true;
  while (feedbackQueue.length > 0 && activeRequests < MAX_CONCURRENT_REQUESTS) {
    const { questionId, questionType, questionData, userAnswer, callback } =
      feedbackQueue.shift();
    activeRequests++;
    try {
      const feedback = await fetchAIFeedback(
        questionId,
        questionType,
        questionData,
        userAnswer
      );
      callback(feedback);
    } finally {
      activeRequests--;
      if (feedbackQueue.length > 0) {
        processQueue();
      }
    }
  }
  isProcessingQueue = false;
};

const fetchAIFeedback = async (
  questionId,
  questionType,
  questionData,
  userAnswer
) => {
  if (
    questionType === "Open-Ended Text" &&
    (!userAnswer || typeof userAnswer !== "string" || userAnswer.trim() === "")
  ) {
    return "Please write your response first to get feedback.";
  }

  if (
    questionType === "Pairing" &&
    (!userAnswer ||
      (Array.isArray(userAnswer) && userAnswer.length === 0) ||
      (typeof userAnswer === "object" && Object.keys(userAnswer).length === 0))
  ) {
    return "No pairs have been created yet. Please make some selections to get feedback.";
  }

  if (
    questionType === "Fill in the Blanks" &&
    (!userAnswer ||
      !Array.isArray(userAnswer) ||
      userAnswer.length === 0 ||
      userAnswer.every((answer) => !answer || answer.trim() === ""))
  ) {
    return "Please fill in at least one blank to get feedback.";
  }

  if (
    questionType === "Multiple Choice" &&
    (!userAnswer ||
      (typeof userAnswer === "object" &&
        (userAnswer.index === null || userAnswer.index === undefined)) ||
      (typeof userAnswer !== "object" &&
        (userAnswer === null || userAnswer === undefined)))
  ) {
    return "Please select an answer to get feedback.";
  }

  if (
    questionType === "Multiple Checkbox" &&
    (!Array.isArray(userAnswer) || userAnswer.length === 0)
  ) {
    return "Please select at least one option to get feedback.";
  }

  if (
    questionType === "Spell the Word" &&
    (!userAnswer || typeof userAnswer !== "string" || userAnswer.trim() === "")
  ) {
    return "Please click on the letters above to spell the word to get feedback.";
  }

  if (
    questionType === "Sentence Ordering" &&
    (!Array.isArray(userAnswer) ||
      userAnswer.length === 0 ||
      userAnswer.every((word) => !word || word.trim() === ""))
  ) {
    return "Please rearrange the words by dragging them to the correct position to get feedback.";
  }

  const maxRetries = 5;
  const baseDelay = 1000;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const prompt = generatePrompt(questionType, questionData, userAnswer);

      const openAIProxy = httpsCallable(functions, "openAIProxy");
      const response = await openAIProxy({
        requestType: "chat",
        params: {
          model: "gpt-4o-mini",
          messages: [{ role: "user", content: prompt }],
        },
      });

      return response.data.choices[0].message.content;
    } catch (error) {
      console.error("Error calling OpenAI Proxy:", error);
      if (
        error.code === "functions/resource-exhausted" ||
        (error.details && error.details.code === 429)
      ) {
        const delay = Math.pow(2, attempt) * baseDelay + Math.random() * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        throw error;
      }
    }
  }

  return "AI feedback is currently unavailable. Please try again later.";
};

const generatePrompt = (questionType, questionData, userAnswer) => {
  const standardFormat = `Please provide your feedback in exactly two sections:

Section 1: Socratic Question
Ask a concise, thought-provoking question that will help me discover my mistake(s). Do not reveal the correct answer in this section, however the question should explicitly quote the mistaken part. After reading the questions, I need to understand my mistake.

Section 2: Direct Feedback
Provide a clear, concise explanation of what the correct answer is and where I made mistakes. If I did not make any mistakes, please say so.`;

  switch (questionType) {
    case "Multiple Choice":
      const formattedUserAnswer = userAnswer.hasOwnProperty("index")
        ? userAnswer
        : {
            index: userAnswer,
            text: questionData.options[userAnswer],
          };

      return `This is a response I gave to a multiple choice question:
Question: ${questionData.question}

Available options:
${questionData.options.map((opt, i) => `${i + 1}. ${opt}`).join("\n")}

Correct answer defined by the teacher:
Option ${questionData.correctAnswers[0].index + 1}: ${
        questionData.correctAnswers[0].text
      }

My attempt:
Option ${formattedUserAnswer.index + 1}: ${formattedUserAnswer.text}

${standardFormat}`;

    case "Multiple Checkbox":
      const formattedUserAnswers = userAnswer[0]?.hasOwnProperty("index")
        ? userAnswer
        : userAnswer.map((index) => ({
            index: index,
            text: questionData.options[index],
          }));

      return `This is a response I gave to a multiple checkbox question:
Question: ${questionData.question}

Available options:
${questionData.options.map((opt, i) => `${i + 1}. ${opt}`).join("\n")}

Correct answers defined by the teacher:
${questionData.correctAnswers
  .map((ans) => `Option ${ans.index + 1}: ${ans.text}`)
  .join("\n")}

My attempt:
${formattedUserAnswers
  .map((sel) => `Option ${sel.index + 1}: ${sel.text}`)
  .join("\n")}

${standardFormat}`;

    case "Fill in the Blanks":
      return `This is a response I gave to a fill in the blanks question:
Fill in the blanks question: ${
        questionData.question
      }\nCorrect answers defined by the teacher: ${questionData.correctAnswers.join(
        ", "
      )}\nMy attempt: ${userAnswer.join(
        ", "
      )}\n\n${standardFormat}\n\nPlease analyze my answers and provide feedback using guiding questions.`;
    case "Pairing": {
      const userPairs = Array.isArray(userAnswer)
        ? userAnswer
        : Object.entries(userAnswer)
            .filter(([left, right]) => left && right && left !== "not paired")
            .map(([left, right]) => ({ left, right }));

      const correctPairsMap = questionData.pairs.reduce((acc, pair) => {
        acc[pair.left] = pair.right;
        return acc;
      }, {});

      const pairAnalysis = userPairs
        .map((pair) => {
          const isCorrect = correctPairsMap[pair.left] === pair.right;
          return `${pair.left} - ${pair.right} (${
            isCorrect ? "Correct" : "Incorrect"
          })`;
        })
        .join("\n");

      const correctPairs = questionData.pairs
        .map((pair) => `${pair.left} - ${pair.right}`)
        .join("\n");

      return `This is a response I gave to a pairing question:
Question: ${questionData.question || "Pair the following items correctly"}

Correct pairs defined by the teacher:
${correctPairs}

My attempt:
${pairAnalysis}

${standardFormat}`;
    }
    case "Sentence Ordering":
      return `This is my response to a sentence ordering question:
Correct order defined by the teacher: ${questionData.correctAnswers.join(" ")}
My attempt: ${userAnswer.join(" ")}

${standardFormat}`;
    case "Spell the Word":
      return `This is my response to a spelling question:
Original sentence: ${questionData.sentence}
Word to spell: ${questionData.correctSpelling}
My attempt to spell the word: ${userAnswer}

Please compare my attempt to the correct spelling and provide a brief feedback only on the spelling of theword.`;
    case "Open-Ended Text": {
      return (
        `Instructions:\n${questionData.content}\n\n` +
        `My response:\n${userAnswer}\n\n` +
        `Read the instructions and analyze my response thoroughly. Highlight specific words or phrases that have issues using these HTML tags:\n` +
        `- <span style="background-color: #fff3cd">yellow for grammar issues - highlight ONLY the specific word(s) with incorrect grammar (e.g., wrong verb tense, article, preposition)</span>\n` +
        `- <span style="background-color: #ffe5d0">orange for spelling issues - highlight ONLY the misspelled word(s)</span>\n` +
        `Important: Do NOT highlight entire sentences: only highlight the exact words or phrases that need correction.\n\n` +
        `Format your response in two sections:\n\n` +
        `1. Highlighted Issues and corrections:\n` +
        `[The original text with specific issues highlighted with the tags I described and corrections in brackets.]\n\n` +
        `2. Details:\n` +
        `[Your detailed feedback in three categories, focusing on:\n` +
        `1) How well the instructions are followed\n` +
        `2) Overall quality of the response\n` +
        `3) Specific explanations for the mistakes and examples of correct usage]\n` +
        `DO NOT use highlighting tags in the 'Details' section. Use "you/your" to address the student directly in the details section.`
      );
    }
    default:
      throw new Error(`Unsupported question type: ${questionType}`);
  }
};

// Function to generate module summary AI feedback
export const generateModuleSummaryFeedback = async (
  studentResponses,
  moduleData,
  setAiFeedback
) => {
  try {
    // Collect all existing feedback from the module
    const existingFeedback = [];

    moduleData.materials
      .filter(
        (material) =>
          material.type === "questions" || material.type === "audio-recorder"
      )
      .forEach((material) => {
        const response = studentResponses[material.questionId];
        if (response?.grade?.feedback) {
          existingFeedback.push({
            questionType: material.questionType || material.type,
            question: material.question || "Audio Recording",
            feedback: response.grade.feedback,
          });
        }
      });

    if (existingFeedback.length === 0) {
      return "No individual feedback found to summarize. Please add feedback to individual questions first.";
    }

    // Create prompt for AI to summarize all feedback
    const prompt = `You are a language teaching assistant. I need you to create an overall module summary based on the individual feedback that has been provided for different questions in this module.

Here is all the individual feedback that has been given:

${existingFeedback
  .map(
    (item, index) => `
${index + 1}. ${item.questionType}: ${item.question}
   Feedback: ${item.feedback}
`
  )
  .join("\n")}

Please create a comprehensive overall module feedback that:
1. Identifies common themes and patterns across the individual feedback
2. Highlights the student's main mistakes in this module
3. Points out the key areas for improvement
4. Provides 2-3 specific actionable recommendations for the student

Keep the tone positive and simple, readable language. The summary should be cohesive and flow naturally, not just a list of individual points.

Format your response as a well-structured paragraph or two.`;

    const openAIProxy = httpsCallable(functions, "openAIProxy");
    const response = await openAIProxy({
      requestType: "chat",
      params: {
        model: "gpt-4o-mini",
        messages: [{ role: "user", content: prompt }],
      },
    });

    const summaryFeedback = response.data.choices[0].message.content;
    setAiFeedback(summaryFeedback);
    return summaryFeedback;
  } catch (error) {
    console.error("Error generating module summary feedback:", error);
    throw new Error("Failed to generate module summary feedback");
  }
};
